"use client";

/**
 * Renders a custom-asset reaction inline as an emoji-sized glyph, PromoDescuentos
 * style: `<i class="sc-emoji sc-emoji--smiley" title=":smiley:">`. The asset URL
 * lives in the stylesheet the root layout injects once (see
 * `buildReactionEmojiCss`), so it never shows up in the comment's markup.
 *
 * Falls back to the native emoji glyph when the reactions-assets flag is off
 * or the reaction has no custom asset. Sized in em-units so it inherits the
 * surrounding font size, like native emojis.
 */
import type { CSSProperties } from "react";
import type { ReactionDefinition } from "@/lib/reactions/types";
import { cn } from "@/lib/utils";
import { useFlag } from "@/lib/flags/client";
import {
  EMOJI_BASE_CLASS,
  reactionEmojiClass,
  safeAssetUrl,
} from "@/lib/reactions/emoji-css";
import { reactionShortcode } from "@/lib/reactions/shortcodes";

export function ReactionEmoji({
  reaction,
  fallback,
  className,
  size = "1em",
  animateOnHover = true,
  inlineAsset = false,
}: {
  reaction: ReactionDefinition;
  /** Glyph shown if the asset is missing. */
  fallback: string;
  className?: string;
  size?: string;
  animateOnHover?: boolean;
  /**
   * Pinta el asset con estilo inline en vez de por clase. Solo para previews
   * de reacciones que aún no están en la hoja global (admin).
   */
  inlineAsset?: boolean;
}) {
  const assetsEnabled = useFlag("global.reactionAssets");
  const assetUrl = safeAssetUrl(reaction.assetUrl);

  if (!assetsEnabled || !assetUrl) {
    return (
      <span
        role="img"
        aria-label={reaction.label}
        className={cn("inline-block align-[-0.125em]", className)}
      >
        {fallback}
      </span>
    );
  }

  const style: CSSProperties | undefined =
    size !== "1em" || inlineAsset
      ? {
          ...(size !== "1em" ? { width: size, height: size } : null),
          ...(inlineAsset ? { backgroundImage: `url("${assetUrl}")` } : null),
        }
      : undefined;

  return (
    <i
      role="img"
      aria-label={reaction.label}
      title={reactionShortcode(reaction)}
      className={cn(
        EMOJI_BASE_CLASS,
        reactionEmojiClass(reaction),
        animateOnHover &&
          "transition-transform duration-150 hover:scale-125 hover:rotate-3",
        className
      )}
      style={style}
    />
  );
}
